import { type GroupNameProblem, groupNameProblem } from '@echo/core';
import { useCreateGroup } from '@echo/core/react';
import { Button, Card, TextField } from '@echo/ui-web';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { CircleAlert } from 'lucide-react';
import { useState } from 'react';
import { ToolbarBack } from '@/components/empty-state-frame';
import { InfoIcon } from '@/components/info-icon';

export const Route = createFileRoute('/_app/account_/groups_/new')({ component: NewGroupPage });

/**
 * Account › My groups › New group (Figma account/groups): the group's name, which everyone
 * invited will see, and what being its Group Leader means. "Create group" with no name asks
 * for one. Core creates it under the Stronger Memory group with the person as its Group Leader,
 * then My groups lists it.
 */
function NewGroupPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const creating = useCreateGroup();
  const [name, setName] = useState('');
  const [checked, setChecked] = useState(false);
  const problem = checked ? groupNameProblem(name) : undefined;
  const problemText: Record<GroupNameProblem, string> = {
    required: t`Group name is required.`,
    'too-short': t`Use at least 2 characters.`,
    'too-long': t`Use 50 characters or fewer.`,
  };
  return (
    <>
      <ToolbarBack
        render={({ className, children }) => (
          <Link to="/account/groups" className={className}>
            {children}
          </Link>
        )}
      />
      <h1 className="text-display-sm font-bold">
        <Trans>New group</Trans>
      </h1>
      <form
        noValidate
        className="flex flex-col gap-section-sm pt-content-md"
        onSubmit={(event) => {
          event.preventDefault();
          setChecked(true);
          if (groupNameProblem(name) || creating.isPending) return;
          creating.mutate(name, {
            onSuccess: (id) =>
              void navigate({ to: '/account/groups/$id', params: { id }, replace: true }),
          });
        }}
      >
        <Card className="flex flex-col gap-content-sm rounded-xl">
          <h2 className="text-title-lg font-bold">
            <Trans>Create a group</Trans>
          </h2>
          <p className="text-body-md text-on-surface-medium">
            <Trans>Start a group to exercise alongside people you know</Trans>
          </p>
          <TextField
            label={t`Group name`}
            placeholder={t`e.g. Brain train group`}
            hint={t`Everyone you invite will see this name.`}
            error={problem ? problemText[problem] : undefined}
            errorIcon={<CircleAlert size={16} aria-hidden />}
            value={name}
            onChange={(event) => {
              setName(event.currentTarget.value);
              setChecked(false);
            }}
          />
          {creating.isError ? (
            <p className="text-body-md text-status-error">
              <Trans>That didn't work. Check your connection and try again.</Trans>
            </p>
          ) : null}
        </Card>
        <p className="flex gap-internal-md text-body-md text-on-surface-medium">
          <InfoIcon />
          <Trans>
            You'll be the Group Leader. You can do your own daily exercises like everyone else, and
            you can invite people and manage the group.
          </Trans>
        </p>
        <div className="flex justify-end">
          <Button type="submit" disabled={creating.isPending}>
            {t`Create group`}
          </Button>
        </div>
      </form>
    </>
  );
}
